/**
 * Hierarchy queries over world records, as pure functions: subtrees, the
 * topmost of a set of entities, and which entity is the level root. Shared by
 * the world's writer and commands and by editor tools that act on selections.
 */
import type { EntityId, EntityRecord } from "./entity";

type Records = ReadonlyMap<EntityId, EntityRecord>;

/** The entity and all its descendants, depth-first with parents before children. */
export function subtreeOf(entities: Records, id: EntityId): EntityId[] {
  const record = entities.get(id);
  if (!record) return [];
  return [id, ...record.children.flatMap((child) => subtreeOf(entities, child))];
}

/**
 * The ids that have no ancestor in the set, in the given order. A selected
 * child goes wherever its selected parent goes (moved, deleted), so acting on
 * it too would act twice.
 */
export function topmostOf(entities: Records, ids: readonly EntityId[]): EntityId[] {
  const set = new Set(ids);
  const hasAncestorIn = (id: EntityId) => {
    for (let parent = entities.get(id)?.parent; parent; parent = entities.get(parent)?.parent) {
      if (set.has(parent)) return true;
    }
    return false;
  };
  return [...set].filter((id) => !hasAncestorIn(id));
}

/** The level root: where new entities go by default. Null for an empty world. */
export function levelRootOf(entities: Records): EntityId | null {
  for (const record of entities.values()) if (isLevelRoot(record)) return record.id;
  return null;
}

/**
 * The level root holds the level's WorldSettings (A-09). It can't be deleted,
 * so a level always keeps something to open and frame.
 */
export function isLevelRoot(record: EntityRecord): boolean {
  return record.components.has("WorldSettings");
}
